import { Children, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { cn } from '@Utils/cn';
import { FlagTriangleRight } from 'lucide-react';

export interface FlowContentAreaProps {
  children: React.ReactNode;
  activeIndex?: number;
  showEndIndicator?: boolean;
  className?: string;
}

const GAP = 24;
const PADDING = 24;
const PREV_RATIO = 0.3;
const FOCUSED_RATIO = 0.5;
const NEXT_RATIO = 0.2;

function getCardWidth(pos: number, cardSpace: number): number {
  switch (pos) {
    case -1:
      return cardSpace * PREV_RATIO;
    case 0:
      return cardSpace * FOCUSED_RATIO;
    case 1:
      return cardSpace * NEXT_RATIO;
    default:
      return 0;
  }
}

function getMarginLeft(pos: number, activeIndex: number): number {
  const isVisible = pos >= -1 && pos <= 1;
  const isFirstVisible = pos === -1 || (pos === 0 && activeIndex === 0);
  return isVisible && !isFirstVisible ? GAP : 0;
}

export function FlowContentArea({
  children,
  activeIndex = 0,
  showEndIndicator = true,
  className,
}: FlowContentAreaProps) {
  const childArray = Children.toArray(children);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const cardSpace = Math.max(0, containerWidth - PADDING * 2 - GAP * 2);
  const itemCount = childArray.length + (showEndIndicator ? 1 : 0);

  const focusedTargetLeft = PADDING + cardSpace * PREV_RATIO + GAP;

  let focusedLeftInTrack = 0;
  for (let j = 0; j < activeIndex; j++) {
    const pos = j - activeIndex;
    focusedLeftInTrack +=
      getCardWidth(pos, cardSpace) + getMarginLeft(pos, activeIndex);
  }
  focusedLeftInTrack += getMarginLeft(0, activeIndex);

  const translateX = focusedTargetLeft - focusedLeftInTrack;

  return (
    <div
      ref={containerRef}
      className={cn('relative z-10 flex-1 bg-zinc-50', className)}
      style={{ overflowX: 'clip' }}
    >
      <div
        className={cn(
          'flex items-start pt-10 pb-10',
          ready && 'transition-transform duration-500 ease-in-out',
        )}
        style={{ transform: `translateX(${translateX}px)` }}
      >
        {childArray.map((child, i) => {
          const pos = i - activeIndex;
          const isVisible = pos >= -1 && pos <= 1;
          const isFocused = pos === 0;

          return (
            <div
              key={i}
              className={cn(
                'shrink-0',
                ready && 'transition-all duration-500 ease-in-out',
              )}
              style={{
                width: getCardWidth(pos, cardSpace),
                marginLeft: getMarginLeft(pos, activeIndex),
                opacity: isVisible ? 1 : 0,
                transform: isFocused ? 'translateY(-24px)' : 'none',
              }}
            >
              {child}
            </div>
          );
        })}

        {showEndIndicator &&
          (() => {
            const endPos = itemCount - 1 - activeIndex;
            const isVisible = endPos >= -1 && endPos <= 1;

            return (
              <div
                className={cn(
                  'flex shrink-0 items-center justify-center',
                  ready && 'transition-all duration-500 ease-in-out',
                )}
                style={{
                  width: getCardWidth(endPos, cardSpace),
                  marginLeft: getMarginLeft(endPos, activeIndex),
                  opacity: isVisible ? 1 : 0,
                }}
              >
                <div className="flex flex-col items-center gap-3 pt-24">
                  <div className="flex size-24 items-center justify-center rounded-full border-2 border-dashed border-zinc-300 bg-white shadow-sm">
                    <FlagTriangleRight className="size-6 text-zinc-400" />
                  </div>
                  <span className="text-sm font-medium text-zinc-400">
                    Flow End
                  </span>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}
